export enum CoverageTypeEnum {
  DENTAL = "dental",
  DENTAL_PPO = "dental-ppo",
  DENTAL_HMO = "dental-hmo",
  VISION = "vision",
  STD = "std",
  LTD = "ltd",
  BASIC_LIFE_ADND = "basicLifeAdnd",
  VOLUNTARY_LIFE_ADND = "voluntaryLifeAdnd",
  ACCIDENT = "accident",
  CRITICAL_ILLNESS = "criticalIllness",
  HOSPITAL_INDEMNITY = "hospitalIndemnity",
  CANCER = "cancer",
  FMLA = "fmla",
  EAP = "eap",
  INDIVIDUAL_DISABILITY_INSURANCE = "individualDisabilityInsurance",
  WHOLE_LIFE = "wholeLife",
}

// Canonical line-of-coverage display order, everywhere in the app. The source
// of truth is the v5 canonical specification's CoverageType enum order (see
// src/libs/backend/canonical/v5/enums_generated.go and ADR 10.049); dental plan
// variants and display-only entries slot in next to their base coverage.
export const COVERAGE_ORDER = [
  "Dental",
  "Dental PPO",
  "Dental HMO",
  "Vision",
  "Vision PPO",
  "Short Term Disability",
  "Long Term Disability",
  "Basic Life/AD&D",
  "Voluntary Life/AD&D",
  "Accident Insurance",
  "Critical Illness Insurance",
  "Hospital Indemnity Insurance",
  "Cancer Insurance",
  "Family Medical Leave Administration",
  "FMLA",
  "State Disability & Leaves",
  "Employee Assistance Program",
  "Individual Disability Insurance",
  "Whole Life",
];

export const COVERAGE_TYPE_ORDER: Record<CoverageTypeEnum, number> = {
  [CoverageTypeEnum.DENTAL]: 0,
  [CoverageTypeEnum.DENTAL_PPO]: 1,
  [CoverageTypeEnum.DENTAL_HMO]: 2,
  [CoverageTypeEnum.VISION]: 3,
  [CoverageTypeEnum.STD]: 5,
  [CoverageTypeEnum.LTD]: 6,
  [CoverageTypeEnum.BASIC_LIFE_ADND]: 7,
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: 8,
  [CoverageTypeEnum.ACCIDENT]: 9,
  [CoverageTypeEnum.CRITICAL_ILLNESS]: 10,
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: 11,
  [CoverageTypeEnum.CANCER]: 12,
  [CoverageTypeEnum.FMLA]: 13,
  [CoverageTypeEnum.EAP]: 14,
  [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]: 15,
  [CoverageTypeEnum.WHOLE_LIFE]: 16,
};

// Sort key for coverage types missing from COVERAGE_TYPE_ORDER: unknown types
// sort after every known one.
export const UNKNOWN_COVERAGE_TYPE_ORDER = Number.MAX_SAFE_INTEGER;

const coverageTypeValues = new Set<string>(Object.values(CoverageTypeEnum));

export const isCoverageType = (value: string): value is CoverageTypeEnum =>
  coverageTypeValues.has(value);

/**
 * Canonical sort index for a coverage type held as a plain string (e.g. an API
 * payload); unknown types sort last.
 */
export const getCoverageTypeOrder = (type: string): number =>
  isCoverageType(type)
    ? COVERAGE_TYPE_ORDER[type]
    : UNKNOWN_COVERAGE_TYPE_ORDER;

export interface HasCoverageType {
  type: CoverageTypeEnum;
}

export const sortRfpCoverages = <T extends HasCoverageType>(
  coverages: T[],
): T[] =>
  [...coverages].sort((a, b) => {
    const orderA = COVERAGE_TYPE_ORDER[a.type] ?? UNKNOWN_COVERAGE_TYPE_ORDER;
    const orderB = COVERAGE_TYPE_ORDER[b.type] ?? UNKNOWN_COVERAGE_TYPE_ORDER;
    return orderA - orderB;
  });

export const sortByCoverage = <T extends { coverage: HasCoverageType }>(
  items: T[],
): T[] =>
  [...items].sort((a, b) => {
    const orderA =
      COVERAGE_TYPE_ORDER[a.coverage.type] ?? UNKNOWN_COVERAGE_TYPE_ORDER;
    const orderB =
      COVERAGE_TYPE_ORDER[b.coverage.type] ?? UNKNOWN_COVERAGE_TYPE_ORDER;
    return orderA - orderB;
  });

// The BTX coverage-summary `status` values
export const COVERAGE_COMPLETION_STATUSES = {
  complete: "complete",
  incomplete: "incomplete",
  awaitingInput: "awaiting-input",
} as const;
